import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import apiService from '../../services/apiService';

const POLL_MS = 60000;

const PRIORITY_TONE = { high: 'red', normal: 'gold', low: 'neutral' };

const formatAge = (ageHours) => {
  if (ageHours === null || ageHours === undefined) return '';
  const hours = Number(ageHours);
  if (Number.isNaN(hours)) return '';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * B1 — compact notifications tray for the forecasting console. Polls the
 * recipient's notifications, shows an unread badge, marks read on open, and
 * routes to each notification's href.
 */
const ForecastNotificationsTray = ({ actor, cycleCode }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    if (!actor) return;
    try {
      const result = await apiService.fetchForecastNotifications({
        cycleCode,
        recipient: actor,
        limit: 25
      });
      setNotifications(result?.notifications || []);
      setUnreadCount(
        result?.unreadCount ??
          (result?.notifications || []).filter((notification) => notification.status === 'unread').length
      );
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load notifications');
    }
  }, [actor, cycleCode]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  // Close the tray on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (notification) => {
    if (notification.status !== 'read') {
      setNotifications((previous) =>
        previous.map((entry) => (entry.id === notification.id ? { ...entry, status: 'read' } : entry))
      );
      setUnreadCount((previous) => Math.max(0, previous - 1));
      try {
        await apiService.updateForecastNotification(notification.id, { actor, status: 'read' });
      } catch (err) {
        /* optimistic — a failed mark-read is refreshed on the next poll */
      }
    }
  };

  const openNotification = async (notification) => {
    await markRead(notification);
    setOpen(false);
    if (notification.href) navigate(notification.href);
  };

  const markAllRead = async () => {
    const unread = notifications.filter((notification) => notification.status === 'unread');
    if (unread.length === 0) return;
    setNotifications((previous) => previous.map((entry) => ({ ...entry, status: 'read' })));
    setUnreadCount(0);
    await Promise.all(
      unread.map((notification) =>
        apiService.updateForecastNotification(notification.id, { actor, status: 'read' }).catch(() => {})
      )
    );
  };

  return (
    <span className="forecast-notif-tray" ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={`forecast-health-chip ${unreadCount > 0 ? 'gold' : 'neutral'}`}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <Bell size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />
        Notifications
        {unreadCount > 0 && <span className="forecast-notif-badge">{unreadCount}</span>}
      </button>

      {open && (
        <div className="forecast-notif-panel forecast-panel" role="menu">
          <div className="forecast-notif-head">
            <span className="forecast-section-code">Notifications</span>
            {notifications.some((notification) => notification.status === 'unread') && (
              <button type="button" className="forecast-chip-button" onClick={markAllRead}>
                Mark all read
              </button>
            )}
          </div>
          {error ? (
            <p className="forecast-submit-note" style={{ color: 'var(--ace-negative, #d9534f)' }}>{error}</p>
          ) : notifications.length === 0 ? (
            <p className="forecast-submit-note">You're all caught up — no notifications.</p>
          ) : (
            <ul className="forecast-notif-list">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className={`forecast-notif-item ${notification.status === 'unread' ? 'unread' : ''}`}
                    onClick={() => openNotification(notification)}
                  >
                    <span className="forecast-notif-item-top">
                      <span className={`forecast-health-chip ${PRIORITY_TONE[notification.priority] || 'neutral'} mini`}>
                        {notification.type}
                      </span>
                      <strong>{notification.title}</strong>
                      <em className="forecast-mono">{formatAge(notification.ageHours)}</em>
                    </span>
                    {notification.body && <span className="forecast-notif-item-body">{notification.body}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
};

export default ForecastNotificationsTray;
